import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import styled from 'styled-components';

// Fix default marker icon paths broken by Vite/webpack bundling
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)
  ._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const Container = styled.div`
  padding: 2rem;
  max-width: 1560px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem 0.625rem 2.5rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: var(--gray-400);
`;

const FilterSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--primary-700);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const StatIconWrapper = styled.div<{ $color: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$color};

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const MapSection = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const MapTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const LeafletWrapper = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 0.5rem;
  overflow: hidden;
  z-index: 0;

  /* Keep Leaflet controls above page chrome but below modals */
  .leaflet-container {
    width: 100%;
    height: 100%;
    font-family: inherit;
  }

  .leaflet-popup-content-wrapper {
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .leaflet-popup-content {
    margin: 0.75rem 1rem;
    font-size: 0.8rem;
    line-height: 1.5;
  }
`;

const WarehousesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const WarehouseCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

const WarehouseHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const WarehouseIcon = styled.div<{ $type: string }>`
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.$type === 'owned'
      ? 'var(--primary-100)'
      : props.$type === 'partner'
        ? 'var(--blue-100)'
        : props.$type === 'fulfillment_center'
          ? 'var(--purple-100)'
          : 'var(--gray-100)'};

  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${(props) =>
      props.$type === 'owned'
        ? 'var(--primary-600)'
        : props.$type === 'partner'
          ? 'var(--blue-600)'
          : props.$type === 'fulfillment_center'
            ? 'var(--purple-600)'
            : 'var(--gray-600)'};
  }
`;

const WarehouseInfo = styled.div`
  flex: 1;
`;

const WarehouseName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.25rem;
`;

const WarehouseCode = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
  font-family: monospace;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) =>
    props.$status === 'active'
      ? 'var(--success-100)'
      : props.$status === 'inactive'
        ? 'var(--gray-100)'
        : props.$status === 'onboarding'
          ? 'var(--blue-100)'
          : 'var(--error-100)'};
  color: ${(props) =>
    props.$status === 'active'
      ? 'var(--success-700)'
      : props.$status === 'inactive'
        ? 'var(--gray-700)'
        : props.$status === 'onboarding'
          ? 'var(--blue-700)'
          : 'var(--error-700)'};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const WarehouseDetails = styled.div`
  margin-bottom: 1rem;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-600);

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: var(--gray-400);
  }
`;

const WarehouseMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  padding: 1rem;
  background: var(--gray-50);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const Metric = styled.div``;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  color: var(--gray-600);
  margin-bottom: 0.25rem;
`;

const MetricValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const WarehouseActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid
    ${(props) =>
      props.$variant === 'danger'
        ? 'var(--error-300)'
        : props.$variant === 'primary'
          ? 'var(--primary-600)'
          : 'var(--gray-300)'};
  background: ${(props) =>
    props.$variant === 'primary' ? 'var(--primary-600)' : 'white'};
  color: ${(props) =>
    props.$variant === 'danger'
      ? 'var(--error-700)'
      : props.$variant === 'primary'
        ? 'white'
        : 'var(--gray-700)'};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.$variant === 'danger'
        ? 'var(--error-50)'
        : props.$variant === 'primary'
          ? 'var(--primary-700)'
          : 'var(--gray-50)'};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'owned' | 'partner' | 'fulfillment_center' | 'dropship';
  status: 'active' | 'inactive' | 'onboarding' | 'suspended';
  address: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  capacity: number;
  available: number;
  dailyOrders: number;
  processingTime: number;
}

const Warehouses: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Sample warehouse data with realistic coordinates
  const warehouses: Warehouse[] = [
    {
      id: '1',
      code: 'WH-US-NY-01',
      name: 'New York Distribution Center',
      type: 'owned',
      status: 'active',
      address: '123 Industrial Blvd',
      city: 'New York, NY 10001',
      country: 'USA',
      latitude: 40.7128,
      longitude: -74.006,
      capacity: 15000,
      available: 8500,
      dailyOrders: 2500,
      processingTime: 18,
    },
    {
      id: '2',
      code: 'WH-UK-LON-01',
      name: 'London Fulfillment Hub',
      type: 'partner',
      status: 'active',
      address: '456 Commerce Way',
      city: 'London E14 5AB',
      country: 'UK',
      latitude: 51.5074,
      longitude: -0.1278,
      capacity: 12000,
      available: 6200,
      dailyOrders: 1800,
      processingTime: 24,
    },
    {
      id: '3',
      code: 'WH-DE-BER-01',
      name: 'Berlin Central Warehouse',
      type: 'fulfillment_center',
      status: 'active',
      address: '789 Logistics Str',
      city: 'Berlin 10178',
      country: 'Germany',
      latitude: 52.52,
      longitude: 13.405,
      capacity: 18000,
      available: 12000,
      dailyOrders: 3200,
      processingTime: 20,
    },
    {
      id: '4',
      code: 'WH-FR-PAR-01',
      name: 'Paris Distribution Center',
      type: 'owned',
      status: 'active',
      address: '321 Avenue du Commerce',
      city: 'Paris 75008',
      country: 'France',
      latitude: 48.8566,
      longitude: 2.3522,
      capacity: 10000,
      available: 5500,
      dailyOrders: 1500,
      processingTime: 22,
    },
    {
      id: '5',
      code: 'WH-ES-MAD-01',
      name: 'Madrid Logistics Center',
      type: 'partner',
      status: 'active',
      address: 'Calle Industrial 100',
      city: 'Madrid 28001',
      country: 'Spain',
      latitude: 40.4168,
      longitude: -3.7038,
      capacity: 9000,
      available: 4800,
      dailyOrders: 1200,
      processingTime: 26,
    },
    {
      id: '6',
      code: 'WH-IT-MIL-01',
      name: 'Milan Distribution Hub',
      type: 'fulfillment_center',
      status: 'onboarding',
      address: 'Via Logistica 50',
      city: 'Milan 20121',
      country: 'Italy',
      latitude: 45.4642,
      longitude: 9.19,
      capacity: 8500,
      available: 8500,
      dailyOrders: 0,
      processingTime: 24,
    },
    {
      id: '7',
      code: 'WH-US-CA-01',
      name: 'Los Angeles West Coast Hub',
      type: 'owned',
      status: 'active',
      address: '555 Pacific Drive',
      city: 'Los Angeles, CA 90021',
      country: 'USA',
      latitude: 34.0522,
      longitude: -118.2437,
      capacity: 20000,
      available: 11000,
      dailyOrders: 3500,
      processingTime: 16,
    },
    {
      id: '8',
      code: 'WH-NL-AMS-01',
      name: 'Amsterdam Distribution Center',
      type: 'partner',
      status: 'active',
      address: 'Handelstraat 25',
      city: 'Amsterdam 1012',
      country: 'Netherlands',
      latitude: 52.3676,
      longitude: 4.9041,
      capacity: 11000,
      available: 7200,
      dailyOrders: 1600,
      processingTime: 20,
    },
  ];

  const filteredWarehouses = warehouses.filter((wh) => {
    const matchesSearch =
      wh.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wh.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wh.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || wh.status === filterStatus;
    const matchesType = filterType === 'all' || wh.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: warehouses.length,
    active: warehouses.filter((wh) => wh.status === 'active').length,
    totalCapacity: warehouses.reduce((sum, wh) => sum + wh.capacity, 0),
    dailyOrders: warehouses.reduce((sum, wh) => sum + wh.dailyOrders, 0),
  };

  return (
    <Container>
      <Header>
        <Title>Warehouse Management</Title>
        <Controls>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search warehouses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          <FilterSelect
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="onboarding">Onboarding</option>
            <option value="suspended">Suspended</option>
          </FilterSelect>
          <FilterSelect
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="owned">Owned</option>
            <option value="partner">Partner</option>
            <option value="fulfillment_center">Fulfillment Center</option>
            <option value="dropship">Dropship</option>
          </FilterSelect>
          <AddButton>
            <PlusIcon />
            Add Warehouse
          </AddButton>
        </Controls>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatLabel>Total Warehouses</StatLabel>
            <StatIconWrapper $color="var(--primary-600)">
              <BuildingOfficeIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Active Warehouses</StatLabel>
            <StatIconWrapper $color="var(--success-600)">
              <CheckCircleIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.active}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Total Capacity (sqm)</StatLabel>
            <StatIconWrapper $color="var(--blue-600)">
              <ChartBarIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.totalCapacity.toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Daily Orders</StatLabel>
            <StatIconWrapper $color="var(--purple-600)">
              <TruckIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.dailyOrders.toLocaleString()}</StatValue>
        </StatCard>
      </StatsGrid>

      <MapSection>
        <MapHeader>
          <MapTitle>Global Warehouse Network</MapTitle>
          <GlobeAltIcon
            style={{
              width: '1.5rem',
              height: '1.5rem',
              color: 'var(--gray-400)',
            }}
          />
        </MapHeader>
        <LeafletWrapper>
          <MapContainer
            center={[48, 10]}
            zoom={3}
            scrollWheelZoom={false}
            style={{ width: '100%', height: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {warehouses.map((wh) => (
              <Marker key={wh.id} position={[wh.latitude, wh.longitude]}>
                <Popup>
                  <strong>{wh.name}</strong>
                  <br />
                  <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>
                    {wh.code}
                  </span>
                  <br />
                  <br />
                  {wh.address}, {wh.city}
                  <br />
                  {wh.country}
                  <br />
                  <br />
                  <span>
                    Capacity used:{' '}
                    <strong>
                      {Math.round(
                        ((wh.capacity - wh.available) / wh.capacity) * 100
                      )}
                      %
                    </strong>
                  </span>
                  <br />
                  <span>
                    Daily orders:{' '}
                    <strong>{wh.dailyOrders.toLocaleString()}</strong>
                  </span>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </LeafletWrapper>
      </MapSection>

      <WarehousesGrid>
        {filteredWarehouses.map((warehouse) => (
          <WarehouseCard key={warehouse.id}>
            <WarehouseHeader>
              <WarehouseIcon $type={warehouse.type}>
                <BuildingOfficeIcon />
              </WarehouseIcon>
              <WarehouseInfo>
                <WarehouseName>{warehouse.name}</WarehouseName>
                <WarehouseCode>{warehouse.code}</WarehouseCode>
              </WarehouseInfo>
              <StatusBadge $status={warehouse.status}>
                {warehouse.status === 'active' && <CheckCircleIcon />}
                {warehouse.status === 'suspended' && <ExclamationCircleIcon />}
                {warehouse.status.charAt(0).toUpperCase() +
                  warehouse.status.slice(1)}
              </StatusBadge>
            </WarehouseHeader>

            <WarehouseDetails>
              <DetailRow>
                <MapPinIcon />
                {warehouse.address}, {warehouse.city}
              </DetailRow>
              <DetailRow>
                <GlobeAltIcon />
                {warehouse.country}
              </DetailRow>
            </WarehouseDetails>

            <WarehouseMetrics>
              <Metric>
                <MetricLabel>Capacity Used</MetricLabel>
                <MetricValue>
                  {Math.round(
                    ((warehouse.capacity - warehouse.available) /
                      warehouse.capacity) *
                      100
                  )}
                  %
                </MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Daily Orders</MetricLabel>
                <MetricValue>
                  {warehouse.dailyOrders.toLocaleString()}
                </MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Available (sqm)</MetricLabel>
                <MetricValue>
                  {warehouse.available.toLocaleString()}
                </MetricValue>
              </Metric>
              <Metric>
                <MetricLabel>Processing Time</MetricLabel>
                <MetricValue>{warehouse.processingTime}h</MetricValue>
              </Metric>
            </WarehouseMetrics>

            <WarehouseActions>
              <ActionButton>
                <MapPinIcon />
                View on Map
              </ActionButton>
              <ActionButton>
                <PencilIcon />
                Edit
              </ActionButton>
              <ActionButton $variant="danger">
                <TrashIcon />
              </ActionButton>
            </WarehouseActions>
          </WarehouseCard>
        ))}
      </WarehousesGrid>
    </Container>
  );
};

export default Warehouses;
